/**
 * Generate a thumbnail image (base64 data URL) from Excalidraw elements.
 * Used for Recent Projects preview in the Entry screen.
 */

import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { exportToCanvas } from "@excalidraw/excalidraw/scene/export";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

const THUMBNAIL_MAX_WIDTH = 640;
const THUMBNAIL_MAX_HEIGHT = 480;

/**
 * Generate a thumbnail data URL from diagram elements.
 * Returns null if elements are empty or generation fails.
 */
export async function generateDiagramThumbnail(
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
): Promise<string | null> {
  const visibleElements = elements.filter((el) => !el.isDeleted);
  if (visibleElements.length === 0) {
    return null;
  }

  try {
    const defaultAppState = getDefaultAppState();
    const mergedAppState = {
      ...defaultAppState,
      ...appState,
      exportScale: 2,
      exportBackground: true,
      viewBackgroundColor:
        appState.viewBackgroundColor ?? defaultAppState.viewBackgroundColor,
    } as AppState;

    const canvas = await exportToCanvas(
      visibleElements,
      mergedAppState,
      files,
      {
        exportBackground: true,
        exportPadding: 40,
        viewBackgroundColor: mergedAppState.viewBackgroundColor,
      },
      (width: number, height: number) => {
        // Allow scale > 1 for small diagrams so output reaches target size (avoids blurry thumbnails)
        const scale = Math.min(
          THUMBNAIL_MAX_WIDTH / width,
          THUMBNAIL_MAX_HEIGHT / height,
        );
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        return { canvas, scale };
      },
    );

    return canvas.toDataURL("image/png");
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[generateThumbnail] Failed:", err);
    }
    return null;
  }
}
